/**
 * StudioFilesPage — V14.1 Sprint B (1c Fichiers)
 * ==============================================
 * Cross-project file explorer. Each project is one branch in the tree with
 * `input/` and `prod/` sub-trees. Reuses the existing `/api/projects/{id}/files`
 * endpoint and groups files by `category` on the client.
 *
 * Architecture note: this page is the placeholder for the future
 * "storage_provider" abstraction (Google Drive / serveur perso). For now it
 * reads from the local Mongo-backed file store transparently.
 */
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, ChevronDown, Folder, FileText, Image, FileType2, Layers, ExternalLink, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  // Inputs (raw user uploads)
  plan_lot:        { label: 'Plans de lots',       group: 'input' },
  plan_etage:      { label: 'Plans d\'étage',      group: 'input' },
  facade:          { label: 'Façades bâtiment',    group: 'input' },
  plan_masse:      { label: 'Plan de masse',       group: 'input' },
  carnet_detail:   { label: 'Carnets de détail',   group: 'input' },
  reference:       { label: 'Références mobilier / texture', group: 'input' },
  cctp:            { label: 'CCTP & descriptifs',  group: 'input' },
  // Production outputs
  shoebox:         { label: 'Shoebox 3D',          group: 'prod' },
  perspective:     { label: 'Perspectives',        group: 'prod' },
  generation:      { label: 'Rendus IA',           group: 'prod' },
  // Fallback
  other:           { label: 'Autres',              group: 'input' },
};

const fileIcon = (cat) => {
  if (['plan_lot', 'plan_etage', 'plan_masse', 'facade', 'carnet_detail'].includes(cat)) return FileType2;
  if (['reference', 'shoebox', 'perspective', 'generation'].includes(cat)) return Image;
  return FileText;
};

const StudioFilesPage = () => {
  const [projects, setProjects] = useState([]);
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [filesByProject, setFilesByProject] = useState({});
  const [loadingProjectId, setLoadingProjectId] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/projects/summary`, { withCredentials: true })
      .then((r) => setProjects(r.data || []))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, []);

  const toggleProject = async (projectId) => {
    const next = new Set(expandedProjects);
    if (next.has(projectId)) { next.delete(projectId); setExpandedProjects(next); return; }
    next.add(projectId);
    setExpandedProjects(next);
    if (!filesByProject[projectId]) {
      setLoadingProjectId(projectId);
      try {
        const r = await axios.get(`${API}/projects/${projectId}/files`, { withCredentials: true });
        setFilesByProject((m) => ({ ...m, [projectId]: r.data || [] }));
      } catch {
        setFilesByProject((m) => ({ ...m, [projectId]: [] }));
      } finally {
        setLoadingProjectId(null);
      }
    }
  };

  return (
    <div className="px-6 py-8 max-w-6xl mx-auto">
      <div className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.20em] text-[#D9A05B] font-medium mb-2">
          Fichiers projet
        </p>
        <h1 className="font-[Chivo] font-light text-[32px] leading-tight tracking-tight text-white">
          Explorateur cross-projets
        </h1>
        <p className="text-white/55 mt-2 text-[14px] max-w-2xl">
          Arborescence par projet, séparée en <strong className="text-white/85">input</strong> (uploads) et{' '}
          <strong className="text-white/85">production</strong> (rendus IA). À terme, tu pourras choisir
          ton espace de stockage (Google Drive, serveur perso…).
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-white/40">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Chargement…
        </div>
      ) : projects.length === 0 ? (
        <p className="text-white/50 italic">Aucun projet à afficher.</p>
      ) : (
        <div className="rounded-xl overflow-hidden" style={{ background: 'var(--c-surface-0)', border: '1px solid var(--c-border)' }}>
          {projects.map((p) => (
            <ProjectNode
              key={p.project_id}
              project={p}
              expanded={expandedProjects.has(p.project_id)}
              onToggle={() => toggleProject(p.project_id)}
              files={filesByProject[p.project_id]}
              isLoading={loadingProjectId === p.project_id}
              onOpenProject={() => navigate(`/projects/${p.project_id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ProjectNode = ({ project, expanded, onToggle, files, isLoading, onOpenProject }) => {
  const grouped = useMemo(() => {
    const out = { input: {}, prod: {} };
    (files || []).forEach((f) => {
      const meta = CATEGORY_LABELS[f.category] || CATEGORY_LABELS.other;
      const grp = meta.group;
      const cat = f.category || 'other';
      if (!out[grp][cat]) out[grp][cat] = { label: meta.label, files: [] };
      out[grp][cat].files.push(f);
    });
    return out;
  }, [files]);

  return (
    <div className="border-b border-white/[0.04] last:border-b-0">
      <div className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-white/[0.02]" onClick={onToggle}
           data-testid={`files-project-${project.project_id}`}>
        {expanded ? <ChevronDown className="w-3.5 h-3.5 text-white/40" /> : <ChevronRight className="w-3.5 h-3.5 text-white/40" />}
        <Folder className={`w-4 h-4 ${expanded ? 'text-[#D9A05B]' : 'text-white/40'}`} />
        <span className="text-white text-[14px] font-medium flex-1 truncate">{project.name}</span>
        <span className="text-[10.5px] text-white/40">{project.files_count || 0} fichiers</span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpenProject(); }}
          className="ml-2 text-[10.5px] text-[#D9A05B]/80 hover:text-[#D9A05B] inline-flex items-center gap-1"
          title="Ouvrir le projet"
        >
          <ExternalLink className="w-3 h-3" />
        </button>
      </div>
      {expanded && (
        <div className="pl-8 pb-3 pr-4">
          {isLoading ? (
            <div className="py-2 text-[12px] text-white/40 flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Chargement…
            </div>
          ) : (
            <>
              <GroupNode group="input" label="input/" data={grouped.input} />
              <GroupNode group="prod"  label="prod/"  data={grouped.prod} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

const GroupNode = ({ group, label, data }) => {
  const cats = Object.entries(data);
  if (cats.length === 0) {
    return (
      <div className="py-1 text-[11.5px] text-white/30 italic">— vide</div>
    );
  }
  return (
    <div className="mt-1">
      <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-[#D9A05B]/70 mt-2 mb-1">
        {label}
      </div>
      {cats.map(([cat, entry]) => (
        <div key={cat} className="ml-3 mt-1">
          <div className="flex items-center gap-1.5 text-[11.5px] text-white/55 mb-0.5">
            <Layers className="w-3 h-3" />
            <span>{entry.label}</span>
            <span className="text-white/30">· {entry.files.length}</span>
          </div>
          <div className="ml-4 space-y-0.5">
            {entry.files.slice(0, 8).map((f) => {
              const Icon = fileIcon(cat);
              return (
                <div key={f.file_id} className="flex items-center gap-1.5 text-[11px] text-white/45 py-0.5"
                     data-testid={`files-file-${f.file_id}`}>
                  <Icon className="w-3 h-3" />
                  <span className="truncate">{f.filename || f.original_filename || f.file_id}</span>
                </div>
              );
            })}
            {entry.files.length > 8 && (
              <div className="text-[10px] text-white/30 italic">… +{entry.files.length - 8} de plus</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default StudioFilesPage;
